import { OverlayRef, Overlay, OverlayPositionBuilder, ConnectedPosition } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, Directive, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewContainerRef } from '@angular/core';
import { CommonTooltipComponent } from '../components/common-tooltip/common-tooltip.component';
import { CommonTooltipData, ToolTipType } from '../../../assets/constants/common-tooltip-data';

@Directive({
  selector: '[pucCommonTooltip]',
  standalone: false
})
export class CommonTooltipDirective implements OnChanges, OnDestroy {
  @Input('pucCommonTooltip') tooltipData!: CommonTooltipData;
  @Input() position: 'left' | 'right' | 'top' | 'bottom' | 'start' | 'bottom-start' | 'bottom-end' = 'left';

  private overlayRef: OverlayRef | null = null;
  private isOverHost = false;
  private isOverTooltip = false;
  private tooltipCompRef: ComponentRef<CommonTooltipComponent> | null = null;
  private closeTimeout: any = null; // Timer reference to handle delays

  constructor(
    private el: ElementRef,
    private overlay: Overlay,
    private positionBuilder: OverlayPositionBuilder,
    private vcr: ViewContainerRef
  ) {
    this.setupHostListeners();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tooltipData']) {
      // If data is cleared externally, force close immediately
      if (!this.tooltipData || this.tooltipData.toolTipType === ToolTipType.NONE) {
        this.closeTooltipImmediate();
        return;
      }

      // If we are hovering the host but tooltip isn't created yet, create it
      if (!this.overlayRef && this.isOverHost) {
        this.openTooltip();
        return;
      }

      // If tooltip is already open, just update the data (prevents flickering)
      if (this.overlayRef && this.tooltipCompRef) {
        this.tooltipCompRef.instance.data = this.tooltipData;
        this.tooltipCompRef.changeDetectorRef.detectChanges();
      }
    }
  }

  private setupHostListeners() {
    const host = this.el.nativeElement;

    host.addEventListener('mouseenter', () => {
      // Cancel the close timer if user moves mouse back to the host
      this.clearCloseTimeout();
      this.isOverHost = true;
      this.openTooltip();
    });

    host.addEventListener('mouseleave', () => {
      this.isOverHost = false;
      this.tryCloseTooltip();
    });
  }

  private openTooltip() {
    if (this.overlayRef) return;

    if (!this.tooltipData || this.tooltipData.toolTipType === 'NONE') {
      return;
    }

    let finalPosition = this.position;
    if (this.tooltipData.toolTipType === ToolTipType.Personal_Widget ||
      this.tooltipData.toolTipType === ToolTipType.Shared_To_Internal) {
      finalPosition = 'bottom-start';
    }

    const positionStrategy = this.positionBuilder
      .flexibleConnectedTo(this.el)
      .withPositions(this.getPositionsFor(finalPosition))
      .withFlexibleDimensions(true)
      .withViewportMargin(8)
      .withPush(true);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.reposition()
    });

    const tooltipPortal = new ComponentPortal(CommonTooltipComponent, this.vcr);
    this.tooltipCompRef = this.overlayRef.attach(tooltipPortal);
    this.tooltipCompRef.instance.data = this.tooltipData;

    this.setupTooltipListeners();
  }

  private setupTooltipListeners() {
    if (!this.overlayRef) return;

    const tooltipEl = this.overlayRef.overlayElement;

    // Enable pointer events so we can detect mouse entry
    tooltipEl.style.pointerEvents = 'auto';

    tooltipEl.addEventListener('mouseenter', () => {
      this.isOverTooltip = true;
      // Cancel closing if user enters the tooltip itself
      this.clearCloseTimeout();

      // Exception: Leaderboard usually closes immediately
      if (this.tooltipData?.toolTipType === ToolTipType.LEADERBOARD_INFO) {
        this.closeTooltipImmediate();
        return;
      }
    });

    tooltipEl.addEventListener('mouseleave', () => {
      this.isOverTooltip = false;
      this.tryCloseTooltip();
    });
  }

  /**
   * Starts a timer to close the tooltip.
   * This gives the user time (300ms) to move from host -> tooltip without it closing.
   */
  private tryCloseTooltip() {
    this.clearCloseTimeout(); // Ensure no duplicate timers

    this.closeTimeout = setTimeout(() => {
      if (!this.isOverHost && !this.isOverTooltip) {
        this.closeTooltipImmediate();
      }
    }, 300); // 300ms delay
  }

  private closeTooltipImmediate() {
    this.overlayRef?.detach();
    this.overlayRef = null;
    this.tooltipCompRef = null;
    this.isOverHost = false;
    this.isOverTooltip = false;
    this.clearCloseTimeout();
  }

  private clearCloseTimeout() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }

  private getPositionsFor(pos: string): ConnectedPosition[] {
    this.position = pos as any;
    return this.getPositions();
  }

  private getPositions(): ConnectedPosition[] {
    switch (this.position) {
      case 'bottom-end':
        return [
          { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
          { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 }
        ];
      case 'bottom-start':
        return [
          { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
          { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8 }
        ];
      case 'start':
        return [
          { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
          { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8 }
        ];
      case 'left':
        return [
          { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -20 },
          { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: -20 }
        ];
      case 'right':
        return [
          { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 12 },
          { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -12 }
        ];
      case 'top':
        return [
          { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
          { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 }
        ];
      case 'bottom':
        return [
          { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
          { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 }
        ];
      default:
        return [
             { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: 0 }
        ];
    }
  }

  ngOnDestroy() {
    this.clearCloseTimeout();
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.tooltipCompRef = null;
  }
}